import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Autocomplete, TextField, Checkbox, FormControlLabel, Tooltip, Modal, Box, Divider } from "@mui/material";
import Text from "../../utils/commonComponent/Custom/text";
import { TextFields, DateInput } from "../../utils/commonComponent/Custom/custominput";
import { AddButton, CancelButton, SaveAndUpdate, UploadButton } from "../../utils/commonComponent/Custom/Button";
import { fetchQuery } from "./../../utils/commonComponent/fetched";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../global";
import { format } from "date-fns";
import moment from "moment";
import { deptmentList } from "./jobRegisterConst";
import { services } from "./jobConstant";
import { getPermissions } from "../rbac/hasAccess";
import { validateRequiredFields } from "../../utils/commonComponent/errorAlert";
import ViewJobRegister from "./ViewJobRegister";


const EditJobRegister = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const PrintView = props.Print == true;
  const navigate = useNavigate();
  const [inputStates, setInputStates] = React.useState({});
  const [employees, setEmployees] = React.useState([]);
  const [settings, setSettings] = React.useState([]);
  const [workOrders, setWorkOrders] = React.useState([]);
  const [originalInputStates, setOriginalInputStates] = React.useState({}); // Store original state  
  const [originalWorkOrders, setOriginalWorkOrders] = React.useState([]);
  const [deletedWorkOrderIds, setDeletedWorkOrderIds] = React.useState([]);
  const [openWorkOrderDialog, setOpenWorkOrderDialog] = React.useState(false);
  const [editingWorkOrderIndex, setEditingWorkOrderIndex] = React.useState(null);
  const [currentWorkOrder, setCurrentWorkOrder] = React.useState({
    workOrderNo: '',
    amendment_No: '',
    workOrderAmount: '',
    workDocument: null,
    start_Date: '',
    end_Date: ''
  });
  const [clientlist, setClientList] = React.useState([]);
  const [clientContacts, setClientContacts] = React.useState([]);
  const [addressList, setAddressList] = React.useState([]);
  const [clientAddressOptions, setClientAddressOptions] = React.useState([]);
  
  const updateInputObject = (key, value) => {
    setInputStates((prevInputStates) => ({
      ...prevInputStates,
      [key]: value,
    }));
  };

  const handleOpenWorkOrderDialog = () => {
    setEditingWorkOrderIndex(null);
    setCurrentWorkOrder({
      workOrderNo: '',
      amendment_No: '',
      workOrderAmount: '',
      workDocument: null,
      start_Date: '',
      end_Date: ''
    });
    setOpenWorkOrderDialog(true);
  };

  const handleEditWorkOrder = (index) => {
    setEditingWorkOrderIndex(index);
    setCurrentWorkOrder({ ...workOrders[index] });
    setOpenWorkOrderDialog(true);
  };

  const handleDeleteWorkOrder = (index) => {
    if (window.confirm("Are you sure you want to delete this work order?")) {
      const workOrder = workOrders[index];

      // If it's an existing work order (has an id), add to deletion list
      if (workOrder.id) {
        setDeletedWorkOrderIds(prev => [...prev, workOrder.id]);
      }

      // Remove from current work orders array
      const newWorkOrders = workOrders.filter((_, i) => i !== index);
      setWorkOrders(newWorkOrders);
    }
  };

  const handleSaveWorkOrder = () => {
    if (!currentWorkOrder.workOrderNo || !currentWorkOrder.workOrderAmount) {
      toast.error("Please fill Work Order No and Amount");
      return;
    }

    if (editingWorkOrderIndex !== null) {
      const newWorkOrders = [...workOrders];
      newWorkOrders[editingWorkOrderIndex] = currentWorkOrder;
      setWorkOrders(newWorkOrders);
    } else {
      setWorkOrders([...workOrders, currentWorkOrder]);
    }

    setOpenWorkOrderDialog(false);
  };
    // Helper function to check if work order has changed
  const hasWorkOrderChanged = (current, original) => {
    if (!original) return true; // New work order
    
    return (
      current.workOrderNo !== original.workOrderNo ||
      current.amendment_No !== original.amendment_No ||
      current.workOrderAmount !== original.workOrderAmount ||
      current.workDocument !== original.workDocument ||
      current.start_Date !== original.start_Date ||
      current.end_Date !== original.end_Date
    );
  };

  // Helper function to check if main form has changed
  const hasMainFormChanged = () => {
    if (params.id == 0) return true; // New record, always save
    
    const keys = Object.keys(inputStates);
    for (let key of keys) {
      if (inputStates[key] !== originalInputStates[key]) {
        return true;
      }
    }
    return false;
  };

  const fetchJobRegisterDetails = async (clients = [], addresses = []) => {
    try {
      const [jobRegister] = await fetchQuery(`select * from jobRegister where id = ${params.id}`);
      setInputStates(jobRegister);
      setOriginalInputStates(jobRegister); // Store original state

      // Fetch work orders for this job register
      if (params.id != 0) {
        const workOrdersData = await fetchQuery(`select * from jobRegisterWorkOrder where jobRegisterId = ${params.id}`);
        setWorkOrders(workOrdersData || []);
        setOriginalWorkOrders(JSON.parse(JSON.stringify(workOrdersData || []))); // Deep clone
        // populate clientContacts and clientAddressOptions using already-fetched data
        if (jobRegister.clientId) {
          // populate clientContacts
          const client = clients.find(c => c.id == jobRegister.clientId);
          if (client?.contact) {
            setClientContacts(JSON.parse(client.contact));
          }
          // populate clientAddressOptions
          if (client?.address) {
            const ids = client.address.split(",").map(id => id.trim()).filter(Boolean);
            setClientAddressOptions(addresses.filter(a => ids.includes(String(a.id))));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching client:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await fetchQuery(`select * from employees where status = 1`);
      setEmployees(data);
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  };

   const fetchClients = async () => {
    try {
      const data = await fetchQuery(`select * from clientMaster where status = 1`);
      setClientList(data);
      return data;
    } catch (error) {
      console.error("Error fetching employees:", error);
      return [];
    }
  };

  const jobRegisterLastId = async () => {
    try {
      const [lastJobRegister] = await fetchQuery(`select id, jobNumber from jobRegister ORDER BY id DESC LIMIT 1`);
      const num = parseInt(lastJobRegister?.jobNumber?.split("/")[1] || 0, 10) + 1;
      const clientInitialsWithoutSpaces = inputStates?.clientInitials?.replace(/\s+/g, "") || "";
      updateInputObject("jobNumber", `${clientInitialsWithoutSpaces}26/${num}`);
    } catch (error) {
      console.error("Error fetching last job register ID:", error);
    }
  };

  const handleFileUpload = (event, stateKey, isWorkOrder = false) => {
    const file = event.target.files[0];
    // added validation only
    if (file && /[&#$*]/.test(file.name)) {
      toast.warning("File name contains special characters. Please rename the file and upload again.");
      return;
    }
    const data = new FormData();
    data.append("fileToUpload", file);
    axiosWithToken
      .post(BASE_URL + `fileUpload/fileUpload.php`, data)
      .then((res) => {
        if (res.status == 200) {
          if (isWorkOrder) {
            setCurrentWorkOrder(prev => ({
              ...prev,
              workDocument: res.data,
            }));
          } else {
            updateInputObject(stateKey, res.data);
          }
          toast.success("File Uploaded Successfully!");
        }
      })
      .catch(() => toast.error("Something went wrong"));
  };

  const fetchSettings = async () => {
    try {
      const res = await fetchQuery(`select * from settings where status = 1`);
      setSettings(res);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAddresses = async () => {
    try {
      const data = await fetchQuery(`select * from addresses`);
      setAddressList(data);
      return data;
    } catch (error) {
      console.error("Error fetching addresses:", error);
      return [];
    }
  };

  const onSubmit = async () => {
    // show alert 
    const fieldsToValidate = [
      { value: inputStates.jobDate, label: "Job Date Required" },
      { value: inputStates.clientInitials, label: "Client Initials Required" },
      { value: inputStates.department, label: "Department Required" },
      { value: inputStates.projectInchargeId, label: "Project InCharge required" },
      { value: inputStates.projectHodId, label: "Project HOD required" },
      { value: inputStates.projectOwnerId, label: "Project Owner required" },
      { value: inputStates.siteName, label: "Site Name required" },
      { value: inputStates.clientName, label: "Client Name Required" },
      { value: inputStates.address, label: "Client Address Required" },
      { value: inputStates.principleClient, label: "Principle Client Required" },
      { value: inputStates.projectName, label: "Project Name required" },
      { value: inputStates.serviceId, label: "Service required" },
    ];
    if (!validateRequiredFields(fieldsToValidate)) {
      return;
    }

    try {
      let jobRegisterId = params.id;
      // Only update main form if it has changed
      if (hasMainFormChanged()) {
      let payload = {
        ...inputStates,
        workOrderValidity: inputStates.workOrderValidity
          ? moment(inputStates.workOrderValidity).format("YYYY-MM-DD")
          : null,
        createdBy: localStorage.getItem("id"),
      };
      const url = BASE_URL + `jobRegister`;

      if (params.id != 0) {
        await axiosWithToken.patch(url + `/${params.id}`, payload);
      } else {
        const response = await axiosWithToken.post(url, payload);
        jobRegisterId = response?.data?.insertId;
      }
      }
      // Delete marked work orders
      if (deletedWorkOrderIds.length > 0) {
        for (const id of deletedWorkOrderIds) {
          try {
            await axiosWithToken.delete(`${BASE_URL}jobRegisterWorkOrder/${id}`);
          } catch (error) {
            console.error("Error deleting work order:", error);
          }
        }
      }
      // Process work orders - only save/update changed ones
      if (workOrders.length > 0) {
        for (const wo of workOrders) {
          const originalWO = originalWorkOrders.find(o => o.id == wo.id);
          // Only process if work order has changed or is new
          if (hasWorkOrderChanged(wo, originalWO)) {
          const woPayload = {
            jobRegisterId: jobRegisterId,
            workOrderNo: wo.workOrderNo,
            amendment_No: wo.amendment_No || '',
            workOrderAmount: wo.workOrderAmount,
            workDocument: wo.workDocument,
            start_Date: wo.start_Date ? moment(wo.start_Date).format("YYYY-MM-DD") : null,
            end_Date: wo.end_Date ? moment(wo.end_Date).format("YYYY-MM-DD") : null
          };

          if (wo.id) {
              // Update existing work order
            await axiosWithToken.patch(`${BASE_URL}jobRegisterWorkOrder/${wo.id}`, woPayload);
          } else {
              // Create new work order
            await axiosWithToken.post(`${BASE_URL}jobRegisterWorkOrder`, woPayload);
            }
          }
        }
      }

      toast.success(
        params.id !== "0"
          ? "Job Register Updated Successfully!"
          : "Job Register Created Successfully!"
      );
      if (params.id == 0) {
        setTimeout(() => navigate("/jobRegister"), 1000);
      } else {
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      fetchEmployees();
      fetchSettings();
      const [clients, addresses] = await Promise.all([fetchClients(), fetchAddresses()]);
      if (params.id != 0) fetchJobRegisterDetails(clients, addresses);
    };
    init();
  }, [params.id]);

  useEffect(() => {
    if (params.id == 0) {
      jobRegisterLastId();
    }
  }, [params.id, inputStates?.clientInitials]);


  // Helper functions for multi-select autocomplete
  const getSelectedDepartments = () => {
    const selectedIds = (inputStates.department || "").split(",").map(id => id.trim()).filter(Boolean);
    return deptmentList.filter(dept => selectedIds.includes(String(dept.id)));
  };

  const getSelectedServices = () => {
    const selectedIds = (inputStates.serviceId || "").split(",").map(id => id.trim()).filter(Boolean);
    return services.filter(service => selectedIds.includes(String(service.id)));
  };

  return PrintView ? (
    <ViewJobRegister
      inputStates={inputStates}
      employees={employees}
      workOrders={workOrders}
      permissions={permissions}
    />
  ) : (
    <div>
      <Text variant="h3" bold className="text-blue-600">
        {params.id == 0 ? "Create New Job Register" : `Update Job Register`}
      </Text>
      <br />

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {/* Job No */}
        <TextFields
          label="Job No"
          value={inputStates.jobNumber || ""}
          disabled
        />

        {/* Job Date */}
        <DateInput
          label="Job Date *"
          value={inputStates.jobDate ? new Date(inputStates.jobDate) : ""}
          onChange={(date) => {
            const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
            updateInputObject("jobDate", formattedDate);
          }}
          maxDate={new Date()}
        />

        {/* Client */}
        <Autocomplete
          size="small"
          options={clientlist}
          getOptionLabel={(option) =>
            `${option.companyName || ""}${option.uniCode ? ` (${option.uniCode})` : ""}`
          }
          value={clientlist.find((c) => c.id == inputStates.clientId) || null}
          onChange={(event, value) => {
            updateInputObject("clientId", value ? value.id : "");
            if (!value) {
              setClientContacts([]);
              return;
            }
            // Only for new Job Register
              updateInputObject("clientInitials", value.clientInitial || "");
              updateInputObject("clientName", value.companyName || "");
              updateInputObject("gstNumber", value.gstNumber || "");
              updateInputObject("siteState", value.state || "");
              // address fetch
              const addrIds = (value.address || "").split(",").map(id => id.trim()).filter(Boolean);
              const resolvedAddresses = addressList.filter(a => addrIds.includes(String(a.id)));
              setClientAddressOptions(resolvedAddresses);

            let contacts = [];
            try {
              contacts = value.contact ? JSON.parse(value.contact) : [];
            } catch (e) {
              contacts = [];
            }
            setClientContacts(contacts);
          }}
          renderInput={(params) => <TextField {...params} label="Client" />}
          disabled={params.id != 0}
        />

          {/* Client Address */}
          <Autocomplete
            size="small"
            options={clientAddressOptions}
            getOptionLabel={(option) => option.address || ""}
            value={clientAddressOptions.find((c) => c.id == inputStates.clientaddress) || null}
            onChange={(event, value) => {
              updateInputObject("clientaddress", value ? value.id : "");
              updateInputObject("address", value ? value.address : "");
            }}
            renderInput={(params) => <TextField {...params} label="Client Address *" />}
            disabled={params.id != 0}
          />

        {/* Contact Person */}
        <Autocomplete
          size="small"
          options={clientContacts}
          getOptionLabel={(option) =>`${option.contactPersonName || ""}`}
          value={clientContacts.find((c) => JSON.stringify(c) === inputStates.clientContactPerson) || null}
          onChange={(event, value) => {
            updateInputObject("clientContactPerson", value ? JSON.stringify(value) : "");
            updateInputObject("contactPerson", value?.contactPersonName || "");
            updateInputObject("ContactNo", value?.contact || "");
          }}
          renderInput={(params) => <TextField {...params} label="Contact Person" />}
          disabled={params.id != 0}
        />
        {/* Client Initial */}
        <TextFields
          label="Client Initial *"
          value={inputStates.clientInitials || ""}
          onChange={(e) => {
            const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, "");
            updateInputObject("clientInitials", val);
          }}
          disabled={params.id != 0}
        />

        {/* Department */}
        <Autocomplete
          multiple
          size="small"
          limitTags={1}
          options={deptmentList}
          getOptionLabel={(option) => option.label || ""}
          value={getSelectedDepartments()}
          onChange={(event, newValue) => {
            const selectedIds = newValue.map((item) => item.id);
            updateInputObject("department", selectedIds.join(","));
          }}
          renderInput={(params) => <TextField {...params} label="Department *" />}
        />

        {/* Project InCharge */}
        <Autocomplete
          size="small"
          options={employees}
          getOptionLabel={(option) => `${option.employeesCode || ""} - ${option.name || ""}`}
          value={employees.find((emp) => emp.id == inputStates.projectInchargeId) || null}
          onChange={(event, value) => {
            updateInputObject("projectInchargeId", value ? value.id : "");
          }}
          renderInput={(params) => <TextField {...params} label="Project InCharge *" />}
        />

        {/* Project HOD */}
        <Autocomplete
          size="small"
          options={employees}
          getOptionLabel={(option) => `${option.employeesCode || ""} - ${option.name || ""}`}
          value={employees.find((emp) => emp.id == inputStates.projectHodId) || null}
          onChange={(event, value) => {
            updateInputObject("projectHodId", value ? value.id : "");
          }}
          renderInput={(params) => <TextField {...params} label="Project HOD *" />}
        />

        {/* Project Owner */}
        <Autocomplete
          size="small"
          options={employees}
          getOptionLabel={(option) => `${option.employeesCode || ""} - ${option.name || ""}`}
          value={employees.find((emp) => emp.id == inputStates.projectOwnerId) || null}
          onChange={(event, value) => {
            updateInputObject("projectOwnerId", value ? value.id : "");
          }}
          renderInput={(params) => <TextField {...params} label="Project Owner *" />}
        />
        
        {/* Site Name */}
        <TextFields
          label="Site Name *"
          value={inputStates.siteName || ""}
          onChange={(e) => updateInputObject("siteName", e.target.value)}
        />
        {/* Site Country */}
        <TextFields
          label="Site Country"
          value={inputStates.country || ""}
          onChange={(e) => updateInputObject("country", e.target.value)}
        />

        {/* Site State */}
        <TextFields
          label="Site State"
          value={inputStates.siteState || ""}
          onChange={(e) => updateInputObject("siteState", e.target.value)}
        />

        {/* Site City */}
        <TextFields
          label="Site City"
          value={inputStates.siteCity || ""}
          onChange={(e) => updateInputObject("siteCity", e.target.value)}
        />

        {/* Service */}
        <Autocomplete
          multiple
          size="small"
          limitTags={1}
          options={services}
          getOptionLabel={(option) => option.label || ""}
          value={getSelectedServices()}
          onChange={(event, newValue) => {
            const selectedIds = newValue.map((item) => item.id);
            updateInputObject("serviceId", selectedIds.join(","));
          }}
          renderInput={(params) => <TextField {...params} label="Service *" />}
        />

        {/* Reference */}
        <TextFields
          label="Reference"
          value={inputStates.reference || ""}
          onChange={(e) => updateInputObject("reference", e.target.value)}
        />

        {/* Reporting Details Heading */}
        <div className="col-span-full">
          <Text variant="h6" bold className="text-blue-700 bg-blue-50 p-2">
            Reporting Details
          </Text>
        </div>

        {/* Client Name */}
        <TextFields
          label="Client Name *"
          value={inputStates.clientName || ""}
          onChange={(e) => updateInputObject("clientName", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* Client Address */}
        <TextFields
          label="Client Address *"
          value={inputStates.address || ""}
          onChange={(e) => updateInputObject("address", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* PAN No */}
        <TextFields
          label="PAN No."
          value={inputStates.panNo || ""}
          onChange={(e) => updateInputObject("panNo", e.target.value)}
        />

        {/* GST No */}
        <TextFields
          label="GST No."
          value={inputStates.gstNumber || ""}
          onChange={(e) => updateInputObject("gstNumber", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* Principle Client */}
        <TextFields
          label="Principle Client *"
          value={inputStates.principleClient || ""}
          onChange={(e) => updateInputObject("principleClient", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* Contact Person Name */}
        <TextFields
          label="Contact Person Name"
          value={inputStates.contactPerson || ""}
          onChange={(e) => updateInputObject("contactPerson", e.target.value)}
        />

        {/* Contact No */}
        <TextFields
          label="Contact No."
          value={inputStates.ContactNo || ""}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "");
            updateInputObject("ContactNo", onlyDigits);
          }}
        />

        {/* Project Name */}
        <TextFields
          label="Project Name *"
          value={inputStates.projectName || ""}
          onChange={(e) => updateInputObject("projectName", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* Billing Details Heading */}
        <div className="col-span-full flex items-center gap-3">
          <Text variant="h6" bold className="text-blue-700 bg-blue-50 p-2">
            Billing Details
          </Text>
          <Tooltip title="Same as Above" arrow>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => {
                    if (e.target.checked) {
                      updateInputObject("billingClientName", inputStates.clientName || "");
                      updateInputObject("billingAddress", inputStates.address || "");
                      updateInputObject("billingpanNo", inputStates.panNo || "");
                      updateInputObject("billinggstNumber", inputStates.gstNumber || "");
                      updateInputObject("billingcontactPerson", inputStates.contactPerson || "");
                      updateInputObject("billingContactNo", inputStates.ContactNo || "");
                    }
                  }}
                />
              }
            />
          </Tooltip>
        </div>

        {/* Billing Client Name */}
        <TextFields
          label="Client Name *"
          value={inputStates.billingClientName || ""}
          onChange={(e) => updateInputObject("billingClientName", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* Billing Client Address */}
        <TextFields
          label="Client Address *"
          value={inputStates.billingAddress || ""}
          onChange={(e) => updateInputObject("billingAddress", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* Billing PAN No */}
        <TextFields
          label="PAN No"
          value={inputStates.billingpanNo || ""}
          onChange={(e) => updateInputObject("billingpanNo", e.target.value)}
        />

        {/* Billing GST No */}
        <TextFields
          label="GST No"
          value={inputStates.billinggstNumber || ""}
          onChange={(e) => updateInputObject("billinggstNumber", e.target.value)}
          multiline
          minRows={1}
          maxRows={10}
        />

        {/* Billing Contact Person Name */}
        <TextFields
          label="Contact Person Name"
          value={inputStates.billingcontactPerson || ""}
          onChange={(e) => updateInputObject("billingcontactPerson", e.target.value)}
        />

        {/* Billing Contact No */}
        <TextFields
          label="Contact No"
          value={inputStates.billingContactNo || ""}
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/\D/g, "");
            updateInputObject("billingContactNo", onlyDigits);
          }}
        />

        {/* Work Order Details Section */}
        <div className="col-span-full flex items-center justify-between">
          <Text variant="h6" bold className="text-blue-700 bg-blue-50 p-2">
            Work Order Details
          </Text>
          <AddButton
            id="btn-add-work-order"
            label="Add Work Order"
            onClick={handleOpenWorkOrderDialog}
          />
        </div>

        {/* Work Order Table */}
        <div className="col-span-full">
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-semibold">Sr.No.</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold">Work Order No</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold">Amendment No</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold">Amount</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold">Start Date</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold">End Date</th>
                  <th className="px-4 py-2 text-left text-sm font-semibold">Document</th>
                  <th className="px-4 py-2 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No work orders added yet
                    </td>
                  </tr>
                ) : (
                  workOrders.map((wo, index) => (
                    <tr key={index} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">{index + 1}</td>
                      <td className="px-4 py-2 text-sm">{wo.workOrderNo}</td>
                      <td className="px-4 py-2 text-sm">{wo.amendment_No || '-'}</td>
                      <td className="px-4 py-2 text-sm">{wo.workOrderAmount}</td>
                      <td className="px-4 py-2 text-sm">{wo.start_Date ? moment(wo.start_Date).format('DD-MM-YYYY') : '-'}</td>
                      <td className="px-4 py-2 text-sm">{wo.end_Date ? moment(wo.end_Date).format('DD-MM-YYYY') : '-'}</td>
                      <td className="px-4 py-2 text-sm">
                        {wo.workDocument ? (
                          <a href={wo.workDocument} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            View
                          </a>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => handleEditWorkOrder(index)}
                          className="text-blue-600 hover:text-blue-800 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteWorkOrder(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Work Order Dialog */}
        {openWorkOrderDialog && (
          <Modal
            open={openWorkOrderDialog}
            onClose={() => setOpenWorkOrderDialog(false)}
          >
            <Box sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "#fff",
              p: 2,
              width: 700,
              borderRadius: 2,
            }}>
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">
                {editingWorkOrderIndex !== null ? 'Edit Work Order' : 'Add Work Order'}
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <TextFields
                  label="Work Order No *"
                  value={currentWorkOrder.workOrderNo}
                  onChange={(e) => setCurrentWorkOrder({ ...currentWorkOrder, workOrderNo: e.target.value })}
                />
                <TextFields
                  label="Amendment No"
                  value={currentWorkOrder.amendment_No}
                  onChange={(e) => setCurrentWorkOrder({ ...currentWorkOrder, amendment_No: e.target.value })}
                />
                <TextFields
                  label="Work Order Amount *"
                  value={currentWorkOrder.workOrderAmount}
                  onChange={(e) => setCurrentWorkOrder({ ...currentWorkOrder, workOrderAmount: e.target.value })}
                />
                <DateInput
                  label="Start Date"
                  value={currentWorkOrder.start_Date ? new Date(currentWorkOrder.start_Date) : ""}
                  onChange={(date) => setCurrentWorkOrder({ ...currentWorkOrder, start_Date: date ? format(date, "yyyy-MM-dd") : "" })}
                />
                <DateInput
                  label="End Date"
                  minDate={currentWorkOrder.start_Date ? new Date(currentWorkOrder.start_Date) : undefined}
                  value={currentWorkOrder.end_Date ? new Date(currentWorkOrder.end_Date) : ""}
                  onChange={(date) => setCurrentWorkOrder({ ...currentWorkOrder, end_Date: date ? format(date, "yyyy-MM-dd") : "" })}
                />
                <UploadButton
                  label=""
                  tooltipLabel="Upload Document"
                  onChange={(e) => handleFileUpload(e, 'workDocument', true)}
                  outline={false}
                  fileUrl={currentWorkOrder.workDocument}
                  showFile={true}
                  onRemove={(e) => setCurrentWorkOrder({ ...currentWorkOrder, workDocument: null})}
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <CancelButton
                  label="Cancel"
                  onClick={() => setOpenWorkOrderDialog(false)}
                />
                <SaveAndUpdate
                  label={editingWorkOrderIndex !== null ? 'Update' : 'Add'}
                  onClick={handleSaveWorkOrder}
                />
              </div>
            </div>
              </Box>
            </Modal>
        )}

       
        <div />
      </div>

      {settings?.find(s => s.keyName === "JobRegisterCompliancesShow")?.value === "true" && (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {/* Compliances Heading */}
        <div className="col-span-full">
          <Text variant="h6" bold className="text-blue-700 bg-blue-50 p-2">
            Compliances
          </Text>
        </div>
        {/* HR Compliances */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(inputStates.inclusionsHRComplaince)}
                onChange={(e) => updateInputObject("inclusionsHRComplaince", e.target.checked)}
              />
            }
            label="HR Compliances"
          />
        </div>

        <div className="col-span-full">
         <Divider sx={{ my: 0.3, }} />
        </div>

        {/* Workmen Compensation Policy */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.workmen_comp_policy) === 1}
                onChange={(e) => updateInputObject("workmen_comp_policy", e.target.checked ? 1 : 0)}
              />
            }
            label="Workmen Compensation Policy"
          />
        </div>
        {Number(inputStates.workmen_comp_policy) === 1 && (
          <>
            <TextFields
              label="Policy Number"
              value={inputStates.workmen_comp_policy_number || ""}
              onChange={(e) => updateInputObject("workmen_comp_policy_number", e.target.value)}
            />
            <DateInput
              label="Start Date"
              value={inputStates.workmen_comp_policy_start ? new Date(inputStates.workmen_comp_policy_start) : ""}
              onChange={(date) => updateInputObject("workmen_comp_policy_start", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <DateInput
              label="End Date"
              minDate={inputStates.workmen_comp_policy_start ? new Date(inputStates.workmen_comp_policy_start) : undefined}
              value={inputStates.workmen_comp_policy_end ? new Date(inputStates.workmen_comp_policy_end) : ""}
              onChange={(date) => updateInputObject("workmen_comp_policy_end", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <UploadButton
              label=""
              tooltipLabel="Upload Workmen Policy"
              onChange={(e) => handleFileUpload(e, "workmen_comp_document")}
              outline={false}
              fileUrl={inputStates.workmen_comp_document}
              showFile={true}
              onRemove={(e) => updateInputObject("workmen_comp_document", null)}
            />
          </>
        )}
        <div className="col-span-full">
         <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* Insurance */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.insurance_policy) === 1}
                onChange={(e) => updateInputObject("insurance_policy", e.target.checked ? 1 : 0)}
              />
            }
            label="Insurance"
          />
        </div>
        {Number(inputStates.insurance_policy) === 1 && (
          <>
            <TextFields
              label="Policy Number"
              value={inputStates.insurance_policy_number || ""}
              onChange={(e) => updateInputObject("insurance_policy_number", e.target.value)}
            />
            <DateInput
              label="Start Date"
              value={inputStates.insurance_policy_start ? new Date(inputStates.insurance_policy_start) : ""}
              onChange={(date) => updateInputObject("insurance_policy_start", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <DateInput
              label="End Date"
              minDate={inputStates.insurance_policy_start ? new Date(inputStates.insurance_policy_start) : undefined}
              value={inputStates.insurance_policy_end ? new Date(inputStates.insurance_policy_end) : ""}
              onChange={(date) => updateInputObject("insurance_policy_end", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <UploadButton
              label=""
              tooltipLabel="Upload Policy"
              onChange={(e) => handleFileUpload(e, "insurance_document")}
              outline={false}
              fileUrl={inputStates.insurance_document}
              showFile={true}
              onRemove={(e) => updateInputObject("insurance_document", null)}
            />
          </>
        )}
        
        <div className="col-span-full">
         <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* PBG */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.pbg_policy) === 1}
                onChange={(e) => updateInputObject("pbg_policy", e.target.checked ? 1 : 0)}
              />
            }
            label="PBG"
          />
        </div>
        {Number(inputStates.pbg_policy) === 1 && (
          <>
            <TextFields
              label="Amount"
              value={inputStates.pbg_amount || ""}
              onChange={(e) => updateInputObject("pbg_amount", e.target.value)}
            />
            <DateInput
              label="Start Date"
              value={inputStates.pbg_policy_start ? new Date(inputStates.pbg_policy_start) : ""}
              onChange={(date) => updateInputObject("pbg_policy_start", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <DateInput
              label="End Date"
              minDate={inputStates.pbg_policy_start ? new Date(inputStates.pbg_policy_start) : undefined}
              value={inputStates.pbg_policy_end ? new Date(inputStates.pbg_policy_end) : ""}
              onChange={(date) => updateInputObject("pbg_policy_end", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <UploadButton
              label=""
              tooltipLabel="Upload PBG"
              onChange={(e) => handleFileUpload(e, "pbg_document")}
              outline={false}
              fileUrl={inputStates.pbg_document}
              showFile={true}
              onRemove={(e) => updateInputObject("pbg_document", null)}
            />
          </>
        )}
        
        <div className="col-span-full">
         <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* BG */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.bg_policy) === 1}
                onChange={(e) => updateInputObject("bg_policy", e.target.checked ? 1 : 0)}
              />
            }
            label="BG"
          />
        </div>
        {Number(inputStates.bg_policy) === 1 && (
          <>
            <TextFields
              label="Amount"
              value={inputStates.bg_amount || ""}
              onChange={(e) => updateInputObject("bg_amount", e.target.value)}
            />
            <DateInput
              label="Start Date"
              value={inputStates.bg_policy_start ? new Date(inputStates.bg_policy_start) : ""}
              onChange={(date) => updateInputObject("bg_policy_start", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <DateInput
              label="End Date"
              minDate={inputStates.bg_policy_start ? new Date(inputStates.bg_policy_start) : undefined}
              value={inputStates.bg_policy_end ? new Date(inputStates.bg_policy_end) : ""}
              onChange={(date) => updateInputObject("bg_policy_end", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <UploadButton
              label=""
              tooltipLabel="Upload BG"
              onChange={(e) => handleFileUpload(e, "bg_document")}
              outline={false}
              fileUrl={inputStates.bg_document}
              showFile={true}
              onRemove={(e) => updateInputObject("bg_document", null)}
            />
          </>
        )}

        <div className="col-span-full">
         <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* Retention */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.retention) === 1}
                onChange={(e) => updateInputObject("retention", e.target.checked ? 1 : 0)}
              />
            }
            label="Retention"
          />
        </div>
        {Number(inputStates.retention) === 1 && (
          <TextFields
            label="Percentage (%)"
            value={inputStates.retention_percentage || ""}
            onChange={(e) => updateInputObject("retention_percentage", e.target.value)}
          />
        )}

        <div className="col-span-full">
         <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* Labour License */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.labour_license) === 1}
                onChange={(e) => updateInputObject("labour_license", e.target.checked ? 1 : 0)}
              />
            }
            label="Labour License"
          />
        </div>
        {Number(inputStates.labour_license) === 1 && (
          <UploadButton
            label=""
            tooltipLabel="Upload License"
            onChange={(e) => handleFileUpload(e, "labour_license_document")}
            outline={false}
            fileUrl={inputStates.labour_license_document}
            showFile={true}
            onRemove={(e) => updateInputObject("labour_license_document", null)}
          />
        )}

        <div className="col-span-full">
         <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* car policy */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.car_policy) === 1}
                onChange={(e) => updateInputObject("car_policy", e.target.checked ? 1 : 0)}
              />
            }
            label="CAR Policy"
          />
        </div>
        {Number(inputStates.car_policy) === 1 && (
          <>
            <TextFields
              label="Policy Number"
              value={inputStates.car_policy_number || ""}
              onChange={(e) => updateInputObject("car_policy_number", e.target.value)}
            />
            <DateInput
              label="Start Date"
              value={inputStates.car_policy_start ? new Date(inputStates.car_policy_start) : ""}
              onChange={(date) => updateInputObject("car_policy_start", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <DateInput
              label="End Date"
              minDate={inputStates.car_policy_start ? new Date(inputStates.car_policy_start) : undefined}
              value={inputStates.car_policy_end ? new Date(inputStates.car_policy_end) : ""}
              onChange={(date) => updateInputObject("car_policy_end", date ? format(date, "yyyy-MM-dd") : "")}
            />
            <UploadButton
              label=""
              tooltipLabel="Upload Car Policy"
              onChange={(e) => handleFileUpload(e, "car_policy_document")}
              outline={false}
              fileUrl={inputStates.car_policy_document}
              showFile={true}
              onRemove={(e) => updateInputObject("car_policy_document", null)}
            />
          </>
        )}
        
        <div className="col-span-full">
         <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* EMD */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.emd) === 1}
                onChange={(e) => updateInputObject("emd", e.target.checked ? 1 : 0)}
              />
            }
            label="EMD"
          />
        </div>
        {Number(inputStates.emd) === 1 && (
          <>
            <TextFields
              label="Description"
              value={inputStates.emd_description || ""}
              onChange={(e) => updateInputObject("emd_description", e.target.value)}
            />
            <TextFields
              label="Amount"
              value={inputStates.emd_amount || ""}
              onChange={(e) => updateInputObject("emd_amount", e.target.value)}
            />
            <UploadButton
              label=""
              tooltipLabel="Upload EMD"
              onChange={(e) => handleFileUpload(e, "emd_document")}
              outline={false}
              fileUrl={inputStates.emd_document}
              showFile={true}
              onRemove={(e) => updateInputObject("emd_document", null)}
            />
          </>
        )}
        
        <div className="col-span-full">
          <Divider sx={{ my: 1.5, mb:-0.5 }} />
        </div>

        {/* DLP */}
        <div className="col-span-full">
          <FormControlLabel
            control={
              <Checkbox
                checked={Number(inputStates.dlp) === 1}
                onChange={(e) => updateInputObject("dlp", e.target.checked ? 1 : 0)}
              />
            }
            label="DLP"
          />
        </div>
        {Number(inputStates.dlp) === 1 && (
          <TextFields
            label="Period (Description)"
            value={inputStates.dlp_period_description || ""}
            onChange={(e) => updateInputObject("dlp_period_description", e.target.value)}
          />
        )}
      </div>
      )}

      <br />
      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          id="btn-cancel"
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.allAccess) && (
          <SaveAndUpdate
            id="btn-add-outline"
            label={params.id == 0 ? "Save" : "Update"}
            onClick={onSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default EditJobRegister;
